import {
  ClassificationLevel,
  type StudySwimlaneSelectDto,
  type StudyTaskMoesSelectDto,
  type StudyTaskMosesSelectDto,
  type StudyTaskReadinessSelectDto,
  type StudyTaskSelectDto,
  type StudyThreadSelectDto,
} from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { ClearableSearchInput } from '@components/ui/clearable-search-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { Skeleton } from '@components/ui/skeleton';
import { DndContext, type DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useApi } from '@hooks/api.hook';
import { errorToast } from '@hooks/toast.hook';
import { queryKeys } from '@queries/keys';
import {
  useGetMeiaStudySwimlaneTasks,
  useGetMeiaThreadSwimlanes,
  useGetMeiaThreadTaskReadiness,
  useUpdateMeiaTask,
} from '@queries/meia.query';
import { useQueryClient } from '@tanstack/react-query';
import { getClassificationLevelLabel } from '@utils/meia-enum.util';
import { type FC, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MeiaP3CoverageStrip } from './meia-p3-coverage-strip.component';
import { MeiaSwimlaneModal } from './meia-swimlane-modal.component';
import { MeiaTaskColumn } from './meia-task-column.component';
import { MeiaTaskModal } from './meia-task-modal.component';

type MeiaTaskCanvasProps = {
  canEdit: boolean;
  missionId: string;
  studyId: string;
  thread: StudyThreadSelectDto;
};

const FILTER_ALL = 'all';

type TaskFilters = {
  classification: string;
  owner: string;
  search: string;
};

const DEFAULT_TASK_FILTERS: TaskFilters = {
  classification: FILTER_ALL,
  owner: FILTER_ALL,
  search: '',
};

const matchesTaskFilters = (task: StudyTaskSelectDto, filters: TaskFilters): boolean => {
  const search = filters.search.trim().toLowerCase();
  if (search && !task.taskName.toLowerCase().includes(search)) return false;
  if (filters.owner !== FILTER_ALL && task.owner !== filters.owner) return false;
  if (filters.classification !== FILTER_ALL && task.classification !== filters.classification) return false;
  return true;
};

const POSITION_GAP = 1000;

type ModalState =
  | { mode: 'closed' }
  | { mode: 'create'; swimlaneId: string }
  | { mode: 'edit'; task: StudyTaskSelectDto };

type StageMoveConfirmState =
  | { mode: 'closed' }
  | {
      mode: 'open';
      fromSwimlaneId: string;
      nextPosition: number;
      task: StudyTaskSelectDto;
      toSwimlaneId: string;
    };

type DragColumnPayload = { swimlaneId: string; type: 'column' };
type DragTaskPayload = { swimlaneId: string; taskId: string; type: 'task' };
type DragPayload = DragColumnPayload | DragTaskPayload;

const isDragPayload = (value: unknown): value is DragPayload => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  if (typeof candidate.swimlaneId !== 'string') return false;
  if (candidate.type !== 'column' && candidate.type !== 'task') return false;
  if (candidate.type === 'task' && typeof candidate.taskId !== 'string') return false;
  return true;
};

export const MeiaTaskCanvas: FC<MeiaTaskCanvasProps> = (props) => {
  const { canEdit, missionId, studyId, thread } = props;
  const threadId = thread.id;
  const { data: swimlanes = [], isLoading: isLoadingSwimlanes } = useGetMeiaThreadSwimlanes({ studyId, threadId });
  const { data: taskReadiness = [] } = useGetMeiaThreadTaskReadiness({ studyId, threadId });
  const { apiGet } = useApi();
  const sortedSwimlanes = useMemo(() => [...swimlanes].sort((a, b) => a.position - b.position), [swimlanes]);
  const { tasks: allThreadTasks } = useGetMeiaStudySwimlaneTasks({ studyId, swimlanes: sortedSwimlanes });
  const readinessBySwimlaneId = useMemo(() => {
    const map = new Map<string, StudyTaskReadinessSelectDto[]>();
    for (const entry of taskReadiness) {
      const list = map.get(entry.swimlaneId) ?? [];
      list.push(entry);
      map.set(entry.swimlaneId, list);
    }
    return map;
  }, [taskReadiness]);

  const [filters, setFilters] = useState<TaskFilters>(DEFAULT_TASK_FILTERS);
  const [modal, setModal] = useState<ModalState>({ mode: 'closed' });
  const [editingSwimlane, setEditingSwimlane] = useState<StudySwimlaneSelectDto | null>(null);
  const [stageMoveConfirm, setStageMoveConfirm] = useState<StageMoveConfirmState>({ mode: 'closed' });
  const closeModal = (): void => setModal({ mode: 'closed' });
  const closeStageMoveConfirm = (): void => setStageMoveConfirm({ mode: 'closed' });

  const ownerOptions = useMemo(
    () =>
      [...new Set(allThreadTasks.map((task) => task.owner).filter((owner): owner is string => Boolean(owner)))].sort(
        (first, second) => first.localeCompare(second),
      ),
    [allThreadTasks],
  );
  const taskCountBySwimlaneId = useMemo(() => {
    const counts = new Map<string, number>();
    for (const task of allThreadTasks) {
      counts.set(task.swimlaneId, (counts.get(task.swimlaneId) ?? 0) + 1);
    }
    return counts;
  }, [allThreadTasks]);
  const taskFilter = useMemo(() => (task: StudyTaskSelectDto) => matchesTaskFilters(task, filters), [filters]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const { mutateAsync: updateTask } = useUpdateMeiaTask();
  const queryClient = useQueryClient();

  const taskHasScopedMeasures = async (taskId: string): Promise<boolean> => {
    const [taskMoes, taskMoses] = await Promise.all([
      queryClient.ensureQueryData({
        queryFn: async () => {
          const data = await apiGet<StudyTaskMoesSelectDto>({
            path: `/v1/intel/studies/${studyId}/tasks/${taskId}/moes`,
          });
          return data.taskMoes;
        },
        queryKey: queryKeys.meia.getTaskMoes(taskId),
      }),
      queryClient.ensureQueryData({
        queryFn: async () => {
          const data = await apiGet<StudyTaskMosesSelectDto>({
            path: `/v1/intel/studies/${studyId}/tasks/${taskId}/moses`,
          });
          return data.taskMoses;
        },
        queryKey: queryKeys.meia.getTaskMoses(taskId),
      }),
    ]);

    return taskMoes.length > 0 || taskMoses.length > 0;
  };

  const moveTask = async (params: {
    fromSwimlaneId: string;
    nextPosition: number;
    task: StudyTaskSelectDto;
    toSwimlaneId: string;
  }): Promise<void> => {
    try {
      await updateTask({
        previousSwimlaneId: params.fromSwimlaneId,
        scoringRole: undefined,
        studyId,
        swimlaneId: params.fromSwimlaneId === params.toSwimlaneId ? undefined : params.toSwimlaneId,
        taskId: params.task.id,
        position: params.nextPosition,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to move task.';
      errorToast({ description: message });
      // Resync both swimlane lists so the optimistic dnd-kit visual snaps back to server truth.
      queryClient.invalidateQueries({ queryKey: queryKeys.meia.getSwimlaneTasks(params.fromSwimlaneId) });
      if (params.fromSwimlaneId !== params.toSwimlaneId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.meia.getSwimlaneTasks(params.toSwimlaneId) });
      }
    }
  };

  const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
    if (!canEdit) return;

    const { active, over } = event;
    if (!over) return;
    if (active.id === over.id) return;

    const activePayload = isDragPayload(active.data.current) ? active.data.current : null;
    const overPayload = isDragPayload(over.data.current) ? over.data.current : null;
    const fromSwimlaneId = activePayload?.swimlaneId ?? null;
    const toSwimlaneId = overPayload?.swimlaneId ?? null;
    if (!fromSwimlaneId || !toSwimlaneId) return;

    const sourceTasks =
      queryClient.getQueryData<StudyTaskSelectDto[]>(queryKeys.meia.getSwimlaneTasks(fromSwimlaneId)) ?? [];
    const targetTasks =
      fromSwimlaneId === toSwimlaneId
        ? sourceTasks
        : (queryClient.getQueryData<StudyTaskSelectDto[]>(queryKeys.meia.getSwimlaneTasks(toSwimlaneId)) ?? []);

    const movedTask = sourceTasks.find((task) => task.id === active.id);
    if (!movedTask) return;

    const targetSorted = [...targetTasks].sort((a, b) => a.position - b.position);
    const overTaskId = overPayload?.type === 'task' ? overPayload.taskId : null;
    const overIndex = overTaskId ? targetSorted.findIndex((task) => task.id === overTaskId) : targetSorted.length;
    const insertIndex = overIndex === -1 ? targetSorted.length : overIndex;

    const before = targetSorted[insertIndex - 1];
    const after = targetSorted[insertIndex];
    let nextPosition: number;
    if (!before && !after) {
      nextPosition = POSITION_GAP;
    } else if (!before && after) {
      nextPosition = after.position - POSITION_GAP;
    } else if (before && !after) {
      nextPosition = before.position + POSITION_GAP;
    } else if (before && after) {
      nextPosition = Math.floor((before.position + after.position) / 2);
    } else {
      nextPosition = POSITION_GAP;
    }

    if (fromSwimlaneId !== toSwimlaneId && (await taskHasScopedMeasures(movedTask.id))) {
      setStageMoveConfirm({
        fromSwimlaneId,
        mode: 'open',
        nextPosition,
        task: movedTask,
        toSwimlaneId,
      });
      return;
    }

    await moveTask({ fromSwimlaneId, nextPosition, task: movedTask, toSwimlaneId });
  };

  const handleConfirmStageMove = async (): Promise<void> => {
    if (stageMoveConfirm.mode !== 'open') return;
    const { fromSwimlaneId, nextPosition, task, toSwimlaneId } = stageMoveConfirm;
    closeStageMoveConfirm();
    await moveTask({ fromSwimlaneId, nextPosition, task, toSwimlaneId });
  };

  const targetStageName =
    stageMoveConfirm.mode === 'open'
      ? (sortedSwimlanes.find((swimlane) => swimlane.id === stageMoveConfirm.toSwimlaneId)?.phaseName ??
        'the new Stage')
      : '';

  if (isLoadingSwimlanes) {
    return <Skeleton className="h-[400px] w-full" />;
  }

  if (sortedSwimlanes.length === 0) {
    return (
      <EmptyStateContainer
        className="min-h-[400px] rounded-md bg-white shadow-sm"
        message={
          <span>
            Author Stages for this Thread in{' '}
            <Link
              className="text-primary underline"
              to={`/meia/${studyId}/p2`}
            >
              P2
            </Link>
            .
          </span>
        }
        title="No Stages"
      />
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-white p-3">
        <ClearableSearchInput
          className="w-56"
          onChange={(value) => setFilters((previous) => ({ ...previous, search: value ?? '' }))}
          placeholder="Filter tasks by name"
          value={filters.search}
        />
        <Select
          onValueChange={(value) => setFilters((previous) => ({ ...previous, owner: value }))}
          value={filters.owner}
        >
          <SelectTrigger className="h-9 w-44 bg-white text-xs">
            <SelectValue placeholder="Owner" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={FILTER_ALL}>All owners</SelectItem>
            {ownerOptions.map((owner) => (
              <SelectItem
                key={owner}
                value={owner}
              >
                {owner}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          onValueChange={(value) => setFilters((previous) => ({ ...previous, classification: value }))}
          value={filters.classification}
        >
          <SelectTrigger className="h-9 w-44 bg-white text-xs">
            <SelectValue placeholder="Classification" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={FILTER_ALL}>All classifications</SelectItem>
            {Object.values(ClassificationLevel).map((level) => (
              <SelectItem
                key={level}
                value={level}
              >
                {getClassificationLevelLabel(level)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex min-h-[520px] flex-1 flex-col">
        <DndContext
          onDragEnd={(event) => void handleDragEnd(event)}
          sensors={sensors}
        >
          <div className="flex min-h-full flex-1 items-stretch gap-3 overflow-x-auto pb-2">
            {sortedSwimlanes.map((swimlane) => (
              <MeiaTaskColumn
                canEdit={canEdit}
                key={swimlane.id}
                onAddTask={(id) => setModal({ mode: 'create', swimlaneId: id })}
                onEditStage={canEdit ? () => setEditingSwimlane(swimlane) : undefined}
                onEditTask={(task) => setModal({ mode: 'edit', task })}
                studyId={studyId}
                swimlane={swimlane}
                taskFilter={taskFilter}
                taskReadiness={readinessBySwimlaneId.get(swimlane.id) ?? []}
              />
            ))}
          </div>
        </DndContext>
      </div>

      <MeiaP3CoverageStrip
        swimlanes={sortedSwimlanes}
        taskCountBySwimlaneId={taskCountBySwimlaneId}
      />

      {editingSwimlane && (
        <MeiaSwimlaneModal
          isOpen={Boolean(editingSwimlane)}
          onClose={() => setEditingSwimlane(null)}
          studyId={studyId}
          swimlane={editingSwimlane}
          thread={thread}
        />
      )}

      {modal.mode !== 'closed' && (
        <MeiaTaskModal
          isOpen
          missionId={missionId}
          onClose={closeModal}
          readOnly={!canEdit}
          studyId={studyId}
          swimlaneId={modal.mode === 'create' ? modal.swimlaneId : modal.task.swimlaneId}
          task={modal.mode === 'edit' ? modal.task : undefined}
        />
      )}

      <Modal
        centered
        footer={{
          rightButton: {
            children: 'Move and clear measures',
            onClick: () => void handleConfirmStageMove(),
            variant: 'destructive',
          },
        }}
        header={{ title: 'Move Task to a different Stage?' }}
        isOpen={stageMoveConfirm.mode === 'open'}
        onClose={closeStageMoveConfirm}
        width="480px"
      >
        <p className="text-sm text-gray-700">
          Moving this Task to <span className="font-semibold">{targetStageName}</span> will clear its current Measure of
          Success and Measure of Effectiveness links, since those are scoped to the original Stage. Required Measures of
          Performance will be kept.
        </p>
      </Modal>
    </>
  );
};
